import { getRandomUUID, runOnAnalyticsReady } from '../helpers.js';
import { attribution } from './values.js';
// Internal
const isBot = () => {
	const url = new URL(window.location.href);

	return url.searchParams.get('utm_medium') === 'aws_bot';
};

const getPageLoadUuid = () => {
	if (!window.document.pageLoadUuid) window.document.pageLoadUuid = getRandomUUID();

	return window.document.pageLoadUuid;
};

const getEventLocation = (event) => {
	const zones = ['upper body', 'mid body', 'lower body'];

	const eventLocation = event.pageY;
	const footerHeight = window.document.getElementById('footer')?.clientHeight || 0;

	const { scrollHeight: bodyHeight = 0 } = window.document.body || {};

	if (!bodyHeight) return 'indeterminate';

	const relevantPageHeight = bodyHeight - footerHeight;
	const pagePercentage = eventLocation / relevantPageHeight;
	const zoneIndex = Math.floor(pagePercentage * zones.length);

	const zoneOverflow = zoneIndex >= zones.length;
	const lastZone = zones[zones.length - 1];

	return zoneOverflow ? lastZone : zones[zoneIndex];
};

const trackWordpressClick = (event) => {
	const eventPath = event.composedPath();

	if (!eventPath) return;

	for (const index in eventPath) {
		const target = eventPath[index];

		if (typeof target.className !== 'string') continue;

		const isHcpButtonOrLink =
			target.className.includes('hcp-custom-btn') || target.className.includes('hcp-custom-link');
		if (isHcpButtonOrLink) continue;

		const hasButtonClass = target.className.includes('btn') || target.className.includes('button');
		const isLink = target.tagName === 'A' && !hasButtonClass;
		const isLinkWithButtonClass = target.tagName === 'A' && hasButtonClass;
		const isButton = target.tagName === 'BUTTON';
		const isSubmission = target.tagName === 'INPUT' && target.getAttribute('type') === 'submit';

		const commonProperties = {
			page_title: window.document.title || '',
			page_url: window.location.href || '',
			page_path: window.location.pathname || '',
			referrer: window.location.referrer || '',
		};

		if (isLink) {
			const href = target.getAttribute('href') || '';

			const isAnchor = !href.match(/(^\/|\.|:)/g);
			const isExternal = href.includes('mailto:') || href.includes('tel:');
			const isHcpLink = href.search(new RegExp('housecallpro.com', 'g')) !== -1;
			const isInternal = href[0] === '/' || isHcpLink;

			let type;
			if (isAnchor) type = 'anchor';
			else if (isInternal) type = 'internal';
			else if (isExternal) type = 'external';
			else type = '';

			const properties = {
				...commonProperties,
				category: 'link',
				email: '',
				label: target.innerText,
				click_url: href,
				click_id: target.getAttribute('id') || '',
				type,
				location: getEventLocation(event),
				pathname: window.location.pathname || '',
			};
			trackEvent('Click', properties);
		} else if (isLinkWithButtonClass || isButton || isSubmission) {
			const buttonName = target.getAttribute('name') || target.innerText || target.value;
			const buttonLabel = target.innerText || target.value;
			const properties = {
				...commonProperties,
				category: '',
				name: buttonName?.toLowerCase() || '',
				label: buttonLabel?.toLowerCase(),
				location: getEventLocation(event) || '',
				pathname: window.location.pathname || '',
			};
			trackEvent('Click', properties);
		}
	}
};

const pathGroup = (path) => {
	if (!path) return '';

	const pathGroups = {
		'': 'Homepage',
		learn: 'Blog',
		compare: 'Compare Pages',
		features: 'Feature Pages',
		industries: 'Industry Pages',
		'mastermind-online': 'Mastermind Online',
		'working-in-the-trades': 'Templates & Licensing',
	};
	const [, rootPath] = path.split('/');

	return pathGroups[rootPath] || '';
};

const identify = async (payload) => {
	if (isBot()) return;

	const userId = localStorage.getItem('ajs_user_id')?.replace(/\\?"/g, '');
	const unknownUser = [null, '', 'null'].includes(userId);

	if (unknownUser) {
		runOnAnalyticsReady((analytics) => {
			analytics.identify(payload);
		});
		return;
	}

	return new Promise((resolve) => {
		runOnAnalyticsReady((analytics) => {
			if (unknownUser) {
				analytics.identify(payload).then(() => {
					resolve();
				});
			} else {
				analytics.identify(userId, payload).then(() => {
					resolve();
				});
			}
		});
	});
};

const getParamsOrEmpty = (params, key) => {
	return params.get(key) || '';
};

const getPageTrackProperties = () => {
	const url = new URL(window.location.href);
	const params = url.searchParams;

	const experimentData = sessionStorage.getItem('ampExpData');

	const properties = {
		path: window.location.pathname,
		referrer: window.document.referrer,
		url: window.location.href,
		mc: getParamsOrEmpty(params, 'mc'),
		msc: getParamsOrEmpty(params, 'msc'),
		gclid: getParamsOrEmpty(params, 'gclid'),
		fbclid: getParamsOrEmpty(params, 'fbclid'),
		utm_source: getParamsOrEmpty(params, 'utm_source'),
		utm_medium: getParamsOrEmpty(params, 'utm_medium'),
		utm_campaign: getParamsOrEmpty(params, 'utm_campaign'),
		campaignId: getParamsOrEmpty(params, 'campaignId'),
		templateId: getParamsOrEmpty(params, 'templateId'),
		experiment_viewed: experimentData ?? 'not_loaded',
		path_group: pathGroup(window.location.pathname),
		website_platform: 'wordpress',
		browser_size: `${window.innerWidth}x${window.innerHeight}`,
		page_load_uuid: getPageLoadUuid(),
	};

	return properties;
};

const trackPage = async () => {
	if (isBot()) return;

	const properties = getPageTrackProperties();

	runOnAnalyticsReady((analytics) => {
		analytics.page(window.location.pathname, properties);
	});
};

// External

const start = async () => {
	if (isBot()) return;

	const attributionValues = await attribution();
	const identifyPayload = {
		first_name: attributionValues?.first_name,
		company_name: attributionValues?.organization_name,
		lead_id: attributionValues?.lead_id,
		hcp_segment_anonymous_id: attributionValues?.hcp_segment_anonymous_id,
		pro_uuid: attributionValues?.pro_uuid,
		authenticated_pro_uuid: attributionValues?.pro_uuid,
		organization_uuid: attributionValues?.organization_uuid,
		authenticated_organization_uuid: attributionValues?.organization_uuid,
		drift_contact_id: attributionValues?.drift_contact_id,
	};
	await identify(identifyPayload);

	trackPage();

	window.document.addEventListener('click', trackWordpressClick);
};

const trackEvent = async (...args) => {
	if (isBot()) return;

	runOnAnalyticsReady((analytics) => {
		analytics.track(...args);
	});
};

export { start, trackEvent, pathGroup, getPageTrackProperties, trackWordpressClick };
